import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, type ConfigAlertas } from '../api';
import { Pantallas } from './Pantallas';

/**
 * Configuración de las alertas del tenant.
 *
 * El secreto del webhook se muestra una única vez, al generarse. Después el
 * portal solo informa que existe: está cifrado y no se puede recuperar. Es el
 * mismo criterio que con los códigos de respaldo del segundo factor, y por la
 * misma razón — un secreto que la pantalla puede volver a mostrar es un
 * secreto que cualquiera con acceso a la sesión se lleva.
 */

const NIVELES = [
  { clave: 'alta', etiqueta: 'Correos para lo grave', placeholder: 'gerencia@tricot.cl' },
  { clave: 'media', etiqueta: 'Correos para lo moderado', placeholder: 'jefatura@tricot.cl' },
  { clave: 'baja', etiqueta: 'Correos para lo leve', placeholder: 'analista@tricot.cl' },
] as const;

function lineas(texto: string): string[] {
  return texto.split('\n').map((c) => c.trim()).filter(Boolean);
}

export function Alertas({ onError }: { onError: (err: unknown) => void }) {
  const [config, setConfig] = useState<ConfigAlertas | null>(null);
  const [correosAlta, setCorreosAlta] = useState('');
  const [correosMedia, setCorreosMedia] = useState('');
  const [correosBaja, setCorreosBaja] = useState('');
  const [webhook, setWebhook] = useState('');
  const [secretoNuevo, setSecretoNuevo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const datos = await api.configAlertas();
      setConfig(datos);
      setCorreosAlta(datos.correosPorSeveridad.alta.join('\n'));
      setCorreosMedia(datos.correosPorSeveridad.media.join('\n'));
      setCorreosBaja(datos.correosPorSeveridad.baja.join('\n'));
      setWebhook(datos.webhook);
    } catch (err) {
      onError(err);
    }
  }, [onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function guardar(regenerarSecreto = false): Promise<void> {
    setGuardando(true);
    setError(null);
    setAviso(null);
    setSecretoNuevo(null);

    try {
      const datos = await api.guardarConfigAlertas({
        correosAlta: lineas(correosAlta),
        correosMedia: lineas(correosMedia),
        correosBaja: lineas(correosBaja),
        webhook: webhook.trim(),
        regenerarSecreto,
      });
      setConfig(datos);
      if (datos.secreto) setSecretoNuevo(datos.secreto);
      else setAviso('Configuración guardada.');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  /**
   * La prueba va a los correos guardados, no a los del formulario: lo que se
   * comprueba es lo que va a usar el servicio de alertas. Si difieren, se avisa
   * antes de mandar nada, para que nadie crea que probó lo que está escribiendo.
   */
  async function probar(): Promise<void> {
    const guardado = config?.correosPorSeveridad ?? { alta: [], media: [], baja: [] };
    const sinGuardar =
      lineas(correosAlta).join('\n') !== guardado.alta.join('\n') ||
      lineas(correosMedia).join('\n') !== guardado.media.join('\n') ||
      lineas(correosBaja).join('\n') !== guardado.baja.join('\n') ||
      webhook.trim() !== (config?.webhook ?? '');
    if (sinGuardar) {
      setError('Hay cambios sin guardar. Guarda primero y después prueba: se prueba lo guardado.');
      return;
    }

    setProbando(true);
    setError(null);
    setAviso(null);
    try {
      const r = await api.probarAlertas();
      const bien: string[] = [];
      const mal: string[] = [];

      if (r.correo) {
        if (r.correo.llego) {
          bien.push(`Correo enviado a ${r.correo.enviados.join(', ')} vía ${r.correo.via}: revisa la casilla.`);
        } else if (r.correo.error) {
          mal.push(`Correo: ${r.correo.error}.`);
        } else {
          // El servidor no tiene proveedor de correo: el mensaje se imprimió en
          // su consola y no llegó a nadie. Es lo más importante que esta
          // prueba puede decir.
          mal.push(
            'Correo: el servidor no tiene proveedor configurado; la prueba se imprimió en su consola y no llegó a ninguna casilla. Las alertas reales tampoco van a llegar hasta que se configure.'
          );
        }
      }

      if (r.webhook) {
        if (r.webhook.ok) {
          bien.push(`Webhook entregado a ${r.webhook.url} (respondió ${r.webhook.estado ?? '2xx'}), firmado con el secreto vigente.`);
        } else {
          mal.push(
            `Webhook: ${r.webhook.motivo ?? 'no se pudo entregar'}` +
              (r.webhook.estado !== undefined ? ` (respondió ${r.webhook.estado})` : '') +
              '.'
          );
        }
      }

      if (bien.length > 0) setAviso(bien.join(' '));
      if (mal.length > 0) setError(mal.join(' '));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo enviar la prueba');
    } finally {
      setProbando(false);
    }
  }

  if (!config) return <p className="vacio">Cargando…</p>;

  const camposPorNivel = {
    alta: { valor: correosAlta, set: setCorreosAlta },
    media: { valor: correosMedia, set: setCorreosMedia },
    baja: { valor: correosBaja, set: setCorreosBaja },
  } as const;

  const hayAlgunCorreoGuardado =
    config.correosPorSeveridad.alta.length > 0 ||
    config.correosPorSeveridad.media.length > 0 ||
    config.correosPorSeveridad.baja.length > 0;

  return (
    <section className="config-alertas">
      <h3>A quién avisar</h3>
      <p className="tenue">
        Se revisa cada cinco minutos. Una anomalía ya avisada no se repite mientras siga activa, y
        se manda un aviso cuando deja de detectarse. Cada nivel tiene su propia lista: un mismo
        correo puede repetirse en más de una si esa persona quiere enterarse de varios niveles.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        {NIVELES.map((n) => {
          const campo = camposPorNivel[n.clave];
          return (
            <label key={n.clave}>
              <span>{n.etiqueta}</span>
              <textarea
                value={campo.valor}
                onChange={(e) => campo.set(e.target.value)}
                rows={3}
                placeholder={n.placeholder}
              />
            </label>
          );
        })}

        <label>
          <span>Webhook</span>
          <input
            type="url"
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder="https://hooks.tuempresa.cl/vox"
          />
        </label>

        <p className="tenue">
          Debe ser https y público. No se admiten direcciones de red interna: el aviso lo envía un
          servicio nuestro desde dentro de la red, y una URL interna permitiría alcanzar cosas que
          no corresponden.
        </p>

        {config.tieneSecreto && !secretoNuevo && (
          <p className="tenue">
            Ya hay un secreto de firma configurado. No se puede volver a mostrar; si lo perdiste,
            genera uno nuevo y actualiza tu receptor.
          </p>
        )}

        {error && <p className="error">{error}</p>}
        {aviso && <p className="aviso-ok">{aviso}</p>}

        <div className="acciones">
          {config.tieneSecreto && (
            <button type="button" className="secundario" onClick={() => void guardar(true)} disabled={guardando}>
              Generar secreto nuevo
            </button>
          )}
          <button type="submit" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            className="secundario"
            onClick={() => void probar()}
            disabled={probando || guardando || (!hayAlgunCorreoGuardado && !config.webhook)}
            title={
              !hayAlgunCorreoGuardado && !config.webhook
                ? 'Guarda primero un correo o un webhook'
                : 'Manda una alerta de muestra por las vías guardadas'
            }
          >
            {probando ? 'Enviando…' : 'Enviar prueba'}
          </button>
        </div>
      </form>

      {secretoNuevo && (
        <div className="secreto-nuevo">
          <h4>Guarda este secreto ahora</h4>
          <p className="tenue">
            Es la única vez que se muestra. Con él tu receptor verifica que el aviso viene de
            nosotros y no de cualquiera.
          </p>
          <code>{secretoNuevo}</code>
          <button onClick={() => void navigator.clipboard?.writeText(secretoNuevo)}>Copiar</button>
        </div>
      )}

      <Pantallas onError={onError} />

      <h3 className="con-espacio">Cómo verificar la firma</h3>
      <p className="tenue">
        Cada envío lleva <code>x-vox-firma</code>, <code>x-vox-marca</code> y{' '}
        <code>x-vox-evento</code>. La firma es HMAC-SHA256 de <code>marca + &quot;.&quot; + cuerpo</code>{' '}
        con tu secreto. La marca viene en formato ISO 8601 —{' '}
        <code>2026-08-23T05:47:35.870Z</code> — así que se parsea con <code>Date.parse</code>, no
        con <code>Number</code>. Rechazá las de más de cinco minutos: es lo que impide que alguien
        reenvíe un aviso capturado.
      </p>
      <pre className="ejemplo">{`const marca = req.headers['x-vox-marca'];

// Primero la frescura: una firma válida de hace dos días sigue siendo válida.
const edad = Date.now() - Date.parse(marca);
if (!Number.isFinite(edad) || edad > 5 * 60_000) rechazar();

const esperada = createHmac('sha256', SECRETO)
  .update(\`\${marca}.\${cuerpo}\`)
  .digest('hex');

const recibida = req.headers['x-vox-firma'].replace('sha256=', '');
if (esperada.length !== recibida.length) rechazar();
if (!timingSafeEqual(Buffer.from(esperada), Buffer.from(recibida))) rechazar();`}</pre>
    </section>
  );
}
